import styled from '@emotion/styled';
import {
  formatDateAndTime,
  formatHumanTimeDurationTwoUnits,
} from '@racemap/utilities/formattings/times';
import { currentEventTimes } from '@racemap/utilities/functions/event';
import type { RacemapEvent } from '@racemap/utilities/types/events';
import type { Immutable } from 'immer';
import { DateTime } from 'luxon';
import type { FC } from 'react';
import type { CurrentEvent } from 'src/store/events/events_reducers';

interface Props {
  event: Immutable<RacemapEvent | CurrentEvent>;
  compact?: boolean;
  color?: string;
  /** Appends the span between start and end as `Δ 2d 12h`. */
  withDuration?: boolean;
}

export const EventTimes: FC<Props> = ({ event, compact = false, color, withDuration = false }) => {
  const startTime = DateTime.fromISO(event.startTime);
  const endTime = DateTime.fromISO(event.endTime);
  const isSameDay = startTime.hasSame(endTime, 'day');

  const visibleElements = event?.modules.timing.visibleElements;
  const [startTimestamp, endTimestamp] = currentEventTimes(event);
  if (!visibleElements.eventTimes) return null;

  const duration =
    withDuration && startTimestamp != null && endTimestamp != null
      ? formatHumanTimeDurationTwoUnits(endTimestamp - startTimestamp)
      : '';

  return (
    <Container compact={compact} color={color}>
      <DateEntry>
        <b>Start</b> {formatDateAndTime(startTimestamp, true)}
      </DateEntry>
      <DateEntry>
        <b>End</b> {formatDateAndTime(endTimestamp, true, isSameDay)}
      </DateEntry>
      {duration !== '' && (
        <DateEntry title="Duration">
          <b>Δ</b> {duration}
        </DateEntry>
      )}
    </Container>
  );
};

const Container = styled.div<{ compact: boolean; color?: string }>`
  display: flex;
  justify-content: space-around;
  font-size: 1em;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  color: ${(props) => props.color ?? 'rgba(0, 0, 0, 0.45)'};
  flex-direction: column;
  align-items: flex-start;

  @media screen and (min-width: 576px) {
    flex-direction: row;
    justify-content: flex-start;
    font-size: 1.1em;
  }

  ${({ compact }) =>
    compact &&
    `
    flex-direction: row;
    justify-content: space-between !important;
  `}
`;

const DateEntry = styled.div`
  padding-right: 5px;
`;
